import React, { useState, useEffect, useCallback } from 'react';
import { Modal, message } from 'antd';
import { FC114 } from '../../../services/scApi';
import { scApi } from '../../../services/scApi';

interface CommissionDetailModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (commissions: Partial<FC114>[]) => void;
  commissions: Partial<FC114>[];
  scNo: string;
  itemSeq: number;
  masterCurrency: string;
}

const CommissionDetailModal: React.FC<CommissionDetailModalProps> = ({
  open, onClose, onSave, commissions, scNo, itemSeq, masterCurrency,
}) => {
  const [rows, setRows] = useState<Partial<FC114>[]>([]);
  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});

  // Reset rows when modal opens
  useEffect(() => {
    if (open) {
      const init = commissions.length > 0
        ? commissions.map(c => ({ ...c }))
        : [];
      setRows(init);
      // Resolve company names for existing rows
      const namesMap: Record<string, string> = {};
      const lookups = init
        .filter(r => r.fc114_11)
        .map(r =>
          scApi.lookupName('customer', r.fc114_11!).then(name => {
            namesMap[r.fc114_11!] = name;
          })
        );
      Promise.all(lookups).then(() => setCompanyNames(namesMap));
    }
  }, [open, commissions]);

  const addRow = useCallback(() => {
    const maxSeq = rows.reduce((max, r) => Math.max(max, r.fc114_03 || 0), 0);
    setRows(prev => [
      ...prev,
      {
        fc114_01: scNo,
        fc114_02: itemSeq,
        fc114_03: maxSeq + 1,
        fc114_11: '',
        fc114_12: '',
        fc114_13: masterCurrency || '',
        fc114_14: 0,
        fc114_15: 0,
        fc114_16: masterCurrency || '',
        fc114_17: 0,
        fc114_18: 0,
      },
    ]);
  }, [rows, scNo, itemSeq, masterCurrency]);

  const deleteRow = useCallback((idx: number) => {
    setRows(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((r, i) => ({ ...r, fc114_03: i + 1 }));
    });
  }, []);

  const updateField = useCallback((idx: number, key: keyof FC114, value: FC114[keyof FC114]) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  }, []);

  const handleCompanyBlur = useCallback((idx: number, code: string) => {
    if (!code.trim()) return;
    scApi.lookupName('customer', code.trim()).then(name => {
      setCompanyNames(prev => ({ ...prev, [code.trim()]: name }));
    });
  }, []);

  const handleOk = () => {
    // BR-V38: FC114_11 required + must have valid lookup name
    for (let i = 0; i < rows.length; i++) {
      const code = (rows[i].fc114_11 || '').trim();
      if (!code) {
        message.error(`第 ${i + 1} 行：公司代號不可空白`);
        return;
      }
      if (!companyNames[code]) {
        message.error(`第 ${i + 1} 行：查無此公司代號!`);
        return;
      }
    }

    // BR-V39: currency must equal masterCurrency (always enforced)
    for (let i = 0; i < rows.length; i++) {
      const recvCurr = (rows[i].fc114_13 || '').trim();
      const payCurr = (rows[i].fc114_16 || '').trim();
      if (!recvCurr || recvCurr !== masterCurrency) {
        message.error(`第 ${i + 1} 行：應收幣別必須為 ${masterCurrency}`);
        return;
      }
      if (!payCurr || payCurr !== masterCurrency) {
        message.error(`第 ${i + 1} 行：應付幣別必須為 ${masterCurrency}`);
        return;
      }
    }

    onSave(rows);
  };

  const thStyle = (width?: number, textAlign?: string): React.CSSProperties => ({
    width: width || undefined,
    padding: '6px 8px',
    textAlign: (textAlign as React.CSSProperties['textAlign']) || undefined,
    background: '#fafafa',
    borderBottom: '1px solid #f0f0f0',
  });

  const tdStyle = (textAlign?: string): React.CSSProperties => ({
    padding: '4px 8px',
    borderBottom: '1px solid #f0f0f0',
    textAlign: (textAlign as React.CSSProperties['textAlign']) || undefined,
  });

  const inputStyle = (width?: string | number, textAlign?: string): React.CSSProperties => ({
    width: width || '100%',
    height: 28,
    fontSize: 13,
    textAlign: (textAlign as React.CSSProperties['textAlign']) || undefined,
    border: '1px solid #d9d9d9',
    borderRadius: 4,
    padding: '0 8px',
  });

  return (
    <Modal
      title={`銷售佣金 - 項次 ${itemSeq}`}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      width={900}
      okText="確認"
      cancelText="取消"
      destroyOnClose
    >
      <div style={{ marginBottom: 8 }}>
        <button
          className="btn btn-success btn-sm"
          onClick={addRow}
          style={{ marginRight: 8 }}
        >
          + 新增佣金
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={thStyle(35, 'center')}>序</th>
              <th style={thStyle(90)}>公司代號</th>
              <th style={thStyle(90)}>公司名稱</th>
              <th style={thStyle(70)}>應收幣別</th>
              <th style={thStyle(75, 'right')}>應收佣金率%</th>
              <th style={thStyle(85, 'right')}>應收佣金額</th>
              <th style={thStyle(70)}>應付幣別</th>
              <th style={thStyle(75, 'right')}>應付佣金率%</th>
              <th style={thStyle(85, 'right')}>應付佣金額</th>
              <th style={thStyle(50, 'center')}>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '16px 8px', color: '#999' }}>
                  尚無佣金明細，請點擊「+ 新增佣金」
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={tdStyle('center')}>
                  {r.fc114_03 || i + 1}
                </td>
                <td style={tdStyle()}>
                  <input
                    type="text"
                    style={inputStyle('100%')}
                    value={r.fc114_11 || ''}
                    onChange={e => updateField(i, 'fc114_11', e.target.value)}
                    onBlur={e => handleCompanyBlur(i, e.target.value)}
                  />
                </td>
                <td style={{ ...tdStyle(), fontSize: 12 }}>
                  {(() => {
                    const code = (r.fc114_11 || '').trim();
                    const name = companyNames[code];
                    if (!code) return <span style={{ color: '#666' }}>--</span>;
                    if (name) return <span style={{ color: '#666' }}>{name}</span>;
                    if (code in companyNames) return <span style={{ color: '#ff4d4f' }}>*查無此代號*</span>;
                    return <span style={{ color: '#666' }}>--</span>;
                  })()}
                </td>
                <td style={tdStyle()}>
                  <input
                    type="text"
                    style={{ ...inputStyle('100%'), background: '#f5f5f5' }}
                    value={r.fc114_13 || ''}
                    disabled
                  />
                </td>
                <td style={tdStyle()}>
                  <input
                    type="number"
                    style={inputStyle('100%', 'right')}
                    value={r.fc114_14 || ''}
                    onChange={e => updateField(i, 'fc114_14', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td style={tdStyle()}>
                  <input
                    type="number"
                    style={inputStyle('100%', 'right')}
                    value={r.fc114_15 || ''}
                    onChange={e => updateField(i, 'fc114_15', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td style={tdStyle()}>
                  <input
                    type="text"
                    style={{ ...inputStyle('100%'), background: '#f5f5f5' }}
                    value={r.fc114_16 || ''}
                    disabled
                  />
                </td>
                <td style={tdStyle()}>
                  <input
                    type="number"
                    style={inputStyle('100%', 'right')}
                    value={r.fc114_17 || ''}
                    onChange={e => updateField(i, 'fc114_17', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td style={tdStyle()}>
                  <input
                    type="number"
                    style={inputStyle('100%', 'right')}
                    value={r.fc114_18 || ''}
                    onChange={e => updateField(i, 'fc114_18', parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td style={tdStyle('center')}>
                  <button
                    className="btn btn-danger btn-sm"
                    style={{ height: 24, padding: '0 8px', fontSize: 12 }}
                    onClick={() => deleteRow(i)}
                  >
                    刪除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
};

export default React.memo(CommissionDetailModal);
